import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import MainNavbar from '../components/MainNavbar';
import MainFooter from '../components/MainFooter';
import { 
  ChevronDown, 
  ChevronUp
} from 'lucide-react';

// Collapsible section component for better UX with long text
const CollapsibleSection = ({ title, children, icon: Icon, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  return (
    <div className="bg-white rounded-lg shadow-sm mb-6">
      <button 
        className="w-full px-8 py-4 flex items-center justify-between text-left border-b border-gray-200 focus:outline-none"
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="flex items-center">
          {Icon && <Icon className="w-5 h-5 mr-3 text-gray-600" />}
          <h2 className="text-2xl font-bold text-gray-800">{title}</h2>
        </div>
        {isOpen ? <ChevronUp className="w-5 h-5 text-gray-500" /> : <ChevronDown className="w-5 h-5 text-gray-500" />}
      </button>
      {isOpen && (
        <div className="p-8">
          <div className="space-y-4 text-gray-600">
            {children}
          </div>
        </div>
      )}
    </div>
  );
};

const Privacy = () => {
  const currentDate = "May 2, 2025"; // Should be dynamically updated
  
  return (
    <div className="font-sans antialiased text-gray-900">
      {/* Navbar */}
      <MainNavbar />
      
      {/* Hero Section */}
      <section className="py-16 bg-gradient-to-r from-yellow-500 via-orange-500 to-red-500 text-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h1 className="text-4xl font-bold mb-4">Privacy Policy</h1>
          <p className="text-xl mb-4 max-w-2xl mx-auto">
            Last updated: {currentDate}
          </p>
        </div>
      </section>
      
      {/* Privacy Policy Content */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-3xl mx-auto px-4">
          <CollapsibleSection title="1. Introduction" defaultOpen={true}>
            <p>
              At Dessino ("we," "our," or "us"), we respect your privacy and are committed to protecting your personal data. 
              This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our website, 
              mobile applications, design tool services, and related offerings (collectively, the "Services").
            </p>
            <p>
              Please read this Privacy Policy carefully. If you do not agree with the terms of this Privacy Policy, 
              please do not access or use our Services.
            </p>
            <p>
              Capitalized terms not defined in this Privacy Policy have the meanings set forth in our 
              <Link to="/terms" className="text-blue-600 hover:underline"> Terms of Service</Link>.
            </p>
          </CollapsibleSection>
          
          <CollapsibleSection title="2. Personal Information We Collect" id="personalinfo">
            <p>
              The personal information we collect depends on how you interact with our Services. We may collect the following categories of information:
            </p>
            
            <h3 className="text-xl font-semibold mt-6 mb-3 text-gray-700">2.1 Information You Provide to Us</h3>
            <ul className="list-disc pl-6 space-y-3">
              <li>
                <span className="font-medium">Account Information:</span> When you create an account, we collect your name, email address, personal website, and profile picture.
              </li>
              <li>
                <span className="font-medium">Payment Information:</span> When you purchase our services, we use third-party payment processors who collect your payment details. We don't receive your full payment information but may receive associated data (like the fact you've paid, last four digits of your card, and country).
              </li>
              <li>
                <span className="font-medium">Communications:</span> We collect information when you contact us, subscribe to newsletters, or provide feedback.
              </li>
              <li>
                <span className="font-medium">Content You Create:</span> We collect designs, files, and other materials you create or upload using our Services.
              </li>
              <li>
                <span className="font-medium">Job Applications:</span> If you apply for employment, we collect information provided in your application.
              </li>
            </ul>
            
            <h3 className="text-xl font-semibold mt-6 mb-3 text-gray-700">2.2 Information Collected Automatically</h3>
<ul className="list-disc pl-6 space-y-3">
  <li>
    <span className="font-medium">Authentication Information:</span> We collect necessary information to authenticate your identity when you log in, managed securely by AWS Authentication services.
  </li>
  <li>
    <span className="font-medium">Payment Processing Information:</span> When you make payments, Stripe collects necessary information to process transactions securely. This may include device information and session data required for fraud prevention.
  </li>
  <li>
    <span className="font-medium">Basic Server Logs:</span> Our servers automatically collect basic information such as IP addresses and request timestamps needed for security and service availability. This information is not used for tracking or profiling.
  </li>
</ul>
            
            <h3 className="text-xl font-semibold mt-6 mb-3 text-gray-700">2.3 Information from Other Sources</h3>
            <p>We may collect information about you from third-party services, organizations, or when your employer provides access to our Services.</p>
          </CollapsibleSection>

          <CollapsibleSection title="3. Cookies and Third-Party Services">
  <p>
    Our service uses authentication mechanisms provided by AWS Authentication services to maintain secure user sessions. 
    Additionally, we use Stripe for payment processing, which sets its own cookies to ensure secure transactions.
  </p>
  
  <h3 className="text-xl font-semibold mt-6 mb-3 text-gray-700">Authentication Cookies</h3>
  <p>
    The authentication tokens are strictly necessary to provide the secure login services you have requested.
    Without these authentication mechanisms, you would not be able to log in securely to access your account.
  </p>
  
  <h3 className="text-xl font-semibold mt-6 mb-3 text-gray-700">Payment Processing Cookies</h3>
  <p>
    Stripe, our payment processor, uses cookies to:
  </p>
  <ul className="list-disc pl-6 space-y-1 mt-2">
    <li>Maintain the security of your payment session</li>
    <li>Prevent fraud and unauthorized transactions</li>
    <li>Remember your selections during the payment process</li>
    <li>Ensure compliance with payment card industry standards</li>
  </ul>
  <p className="mt-3">
    These cookies are necessary for processing payments securely on our platform. For more information about 
    how Stripe handles your data, please see the <a href="https://stripe.com/privacy" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">Stripe Privacy Policy</a>.
  </p>
  
  <h3 className="text-xl font-semibold mt-6 mb-3 text-gray-700">Our Cookie Practices</h3>
  <p>
    We do not use cookies for advertising, analytics, or marketing purposes. We do not track 
    your activities across other websites or services.
  </p>
  <p className="mt-3">
    The cookies used on our site are strictly necessary to provide the services you have requested 
    and therefore do not require your explicit consent under applicable privacy regulations.
  </p>
</CollapsibleSection>
          
          <CollapsibleSection title="4. How We Use Your Information">
            <p>We use your personal information for the following purposes:</p>
            <ul className="list-disc pl-6 space-y-3">
  <li>To provide, maintain, and improve our Services</li>
  <li>To authenticate users and personalize your experience</li>
  <li>To process transactions and send related information</li>
  <li>To communicate with you about your account, updates, and support</li>
  <li>To secure our Services and protect against fraud</li>
  <li>To comply with legal obligations</li>
</ul>
            
            <h3 className="text-xl font-semibold mt-6 mb-3 text-gray-700">Legal Basis for Processing (for EEA, UK, and Swiss Users)</h3>
            <p>If you are located in the EEA, UK, or Switzerland, our legal basis for collecting and using your personal information includes:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li><span className="font-medium">Performance of Contract:</span> Processing necessary for the performance of our contract with you</li>
              <li><span className="font-medium">Legitimate Interests:</span> Processing necessary for our legitimate interests, such as improving our Services</li>
              <li><span className="font-medium">Consent:</span> Where you have given consent for specific processing activities</li>
              <li><span className="font-medium">Legal Obligation:</span> Processing necessary to comply with our legal obligations</li>
            </ul>
            <p className="mt-3">
              When we process based on consent, you have the right to withdraw consent at any time without affecting the lawfulness of processing based on consent before withdrawal.
            </p>
          </CollapsibleSection>
          
          <CollapsibleSection title="5. Sharing Your Information">
            <p>We may share your information with the following categories of recipients:</p>
            <ul className="list-disc pl-6 space-y-3">
              <li>
                <span className="font-medium">Other Users:</span> When you collaborate with others, certain information may be visible to other users.
              </li>
              <li>
                <span className="font-medium">Service Providers:</span> We share information with vendors who help us provide our Services.
              </li>
              <li>
                <span className="font-medium">Your Organization:</span> If you use our Services through an organization, we may share information with that organization.
              </li>
              <li>
                <span className="font-medium">For Legal Reasons:</span> We may disclose information when required by law or to protect rights and safety.
              </li>
              <li>
                <span className="font-medium">Business Transfers:</span> If we're involved in a merger, acquisition, or sale of assets, your information may be transferred.
              </li>
              <li>
                <span className="font-medium">With Your Consent:</span> We may share information for other purposes with your consent.
              </li>
            </ul>
          </CollapsibleSection>
          
          <CollapsibleSection title="6. International Data Transfers">
            <p>
              Your information may be transferred to and processed in countries other than your country of residence, including the United States where our servers are located. These countries may have data protection laws that differ from those in your country.
            </p>
            <p className="mt-3">
              When we transfer personal data from the EEA, UK, or Switzerland to countries that haven't received adequacy decisions, we implement appropriate safeguards such as Standard Contractual Clauses approved by the European Commission, UK, or Swiss authorities.
            </p>
          </CollapsibleSection>
          
          <CollapsibleSection title="7. Your Choices" id="choices">
            <p>You have several choices regarding your personal information:</p>
            <ul className="list-disc pl-6 space-y-3">
  <li>
    <span className="font-medium">Account Information:</span> You can update your account information through your account settings.
  </li>
  <li>
    <span className="font-medium">Account Deletion:</span> You can request deletion of your account by contacting us directly.
  </li>
  <li>
    <span className="font-medium">Browser Settings:</span> You can manage certain preferences through your browser settings, including cookie management.
  </li>
</ul>
          </CollapsibleSection>
          
          <CollapsibleSection title="8. Your Privacy Rights" id="rights">
            <p>Depending on your location, you may have the right to:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Access and receive a copy of your personal information</li>
              <li>Correct inaccurate personal information</li>
              <li>Delete your personal information</li>
              <li>Object to or restrict certain processing activities</li>
              <li>Data portability (receive your data in a structured, machine-readable format)</li>
              <li>Withdraw consent where processing is based on consent</li>
              <li>Lodge a complaint with a supervisory authority (for EEA residents)</li>
            </ul>
            <p className="mt-3">
              To exercise these rights, please contact us using the information in the "Contact Us" section below.
              We will respond to your request in accordance with applicable laws.
            </p>
          </CollapsibleSection>
          
          <CollapsibleSection title="9. Data Security">
            <p>
              We implement appropriate technical and organizational measures to protect your personal information. 
              However, no method of transmission over the internet or electronic storage is 100% secure. 
              While we strive to use commercially acceptable means to protect your personal information, 
              we cannot guarantee absolute security.
            </p>
          </CollapsibleSection>
          
          <CollapsibleSection title="10. Data Retention">
            <p>
              We retain your personal information for as long as necessary to fulfill the purposes outlined in this Privacy Policy, 
              unless a longer retention period is required or permitted by law. The specific retention periods depend on the nature 
              of the information and why it is collected and processed.
            </p>
            <p className="mt-3">
              When we no longer need your personal information, we will either delete it or anonymize it. If deletion isn't immediately 
              possible (for example, because your information has been stored in backup archives), we'll securely store your information 
              and isolate it from further processing until deletion is possible.
            </p>
          </CollapsibleSection>
          
          <CollapsibleSection title="11. Children's Information">
            <p>
              Our Services are not directed to children under 13 (or higher age as required by local law). 
              We don't knowingly collect personal information from children under these ages. If you believe 
              we have collected information from a child, please contact us, and we will promptly delete such information.
            </p>
            <p className="mt-3">
              In certain cases, students under 13 may use our Services through an agreement with their educational institution. 
              For details about our processing of children's personal information in the United States, please contact us.
            </p>
          </CollapsibleSection>
          
          <CollapsibleSection title="12. Notice for California Residents" id="california">
            <p>
              Under the California Consumer Privacy Act (CCPA), California residents have specific rights regarding their personal information.
            </p>
            <h3 className="text-xl font-semibold mt-6 mb-3 text-gray-700">Categories of Personal Information Collected</h3>
            <p>
  We collect limited categories of personal information described in Section 2, primarily consisting of identifiers (such as name and email), authentication information, and payment transaction data. We do not collect or process information about internet activity beyond basic server logs, and we do not create profiles or inferences about your preferences.
</p>
            
            <h3 className="text-xl font-semibold mt-6 mb-3 text-gray-700">California Privacy Rights</h3>
            <p>California residents have the right to:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Know what personal information is collected, used, shared, or sold</li>
              <li>Delete personal information held by businesses</li>
              <li>Opt-out of the sale of personal information</li>
              <li>Non-discrimination for exercising their CCPA rights</li>
            </ul>
            <p className="mt-3">
              To exercise these rights, please contact us as described in the "Contact Us" section.
            </p>
          </CollapsibleSection>
          
          <CollapsibleSection title="13. Changes to this Privacy Policy">
            <p>
              We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy 
              on this page and updating the "Last Updated" date. You are advised to review this Privacy Policy periodically for any changes.
            </p>
            <p className="mt-3">
              For significant changes, we will provide additional notice, such as an email notification or an in-app alert.
            </p>
          </CollapsibleSection>
          
          <CollapsibleSection title="14. Contact Us" id="contact">
            <p>
              If you have questions about this Privacy Policy or would like to exercise your rights, please contact us at:
            </p>
            <div className="bg-gray-50 p-4 rounded-md mt-3">
              <p>Email: privacy@dessino.ai</p>
            </div>
          </CollapsibleSection>
        </div>
      </section>
      
      {/* Footer */}
      <MainFooter />
    </div>
  );
};

export default Privacy;